import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslate } from 'react-translate';
import { useSelector } from 'react-redux';
import { useHistory, useParams, useRouteMatch } from 'react-router';
import { Scrollbars } from 'react-custom-scrollbars';
import { Formik, Field } from 'formik';
import InputDefault from '../InputDefault/InputDefault';
import SchemaService from '../../services/schema';
import Tooltip from '../Tooltip/Tooltip';
import GroupItem from './GroupItem';
import Active from './Active/Active';
import ClickOutHandler from 'react-clickout-handler';

import * as S from './style';

const { gtag } = window;

const callGtag = (action, user_id, business_id, group_id) => {
  gtag('event', 'click', {
    event_category: 'breadcrumb',
    event_label: action,
    user_id,
    business_id,
    group_id: group_id ?? undefined,
  });
};

const buildChildNodes = (node) => {
  const copy = node;
  for (let i = 0; i < node.length; i += 1) {
    if (node[i].child_groups_count > 0) {
      copy[i].hasChild = true;
    }
  }
  return copy;
};
function BreadCrumb({ data, redirectUrl }) {
  const authenticationReducer = useSelector((state) => state.authenticationReducer);
  const { businessId, dataUser } = authenticationReducer;

  const genericReducer = useSelector((state) => state.genericReducer);
  const { breadCrumb } = genericReducer;
  const t = useTranslate('BreadCrumb');
  const history = useHistory();
  const { path } = useRouteMatch();
  const { code, module, group } = useParams();

  const [showSelector, setShowSelector] = useState(false);
  const [actualGroup, setActualGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState([]);
  const [parent, setParent] = useState(null);
  const [hasParent, setHasParent] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (selectedGroup === 'root') {
      SchemaService.getChildGroups(code).then((response) => {
        const children = buildChildNodes(response.data);
        setActualGroup({ name: 'Todos os grupos', source_id: 'root', children });
      });
    }
  }, [selectedGroup, code]);

  const isAdmin = useMemo(() => dataUser.role === 'admin', [dataUser.role]);

  useEffect(() => {
    setSelectedGroup(code);
    if (isAdmin && code !== 'root') {
      SchemaService.getParents('1.1', code).then((response) => {
        if (response.data?.length) {
          setParent(response.data[0]);
        } else {
          setParent({
            source_id: 'root',
            name: 'Todos os grupos',
          });
        }
        setHasParent(true);
      });
    }
  }, [isAdmin, code]);

  useEffect(() => {
    if (breadCrumb.data.currentCode) {
      const temp = breadCrumb.data.currentCode;
      SchemaService.getChildGroups(code).then((response) => {
        const children = buildChildNodes(response.data);
        temp.children = children;
        setActualGroup(temp);
      });
    }
  }, [breadCrumb.data.currentCode, code]);

  useEffect(() => {
    if (!breadCrumb.isLoading) {
      if (breadCrumb.data.parents && breadCrumb.data.parents.length) {
        setHasParent(true);
        setParent(breadCrumb.data.parents[0]);
      } else if (!breadCrumb.data.parents && isAdmin) {
        setHasParent(false);
      }
    }
  }, [breadCrumb.data.parents, breadCrumb.isLoading, isAdmin]);

  const changeCodeUrl = useCallback(
    (code) => {
      let newUrl = path;
      if (redirectUrl) {
        newUrl = `/:code/${redirectUrl}`;
      }
      newUrl = newUrl.replace(':code', code);
      newUrl = newUrl.replace(':module', module);
      newUrl = newUrl.replace(':group', group);
      history.push(newUrl);
    },
    [group, history, module, path, redirectUrl]
  );

  const openGroup = useCallback(
    (name, ID) => {
      setParent(actualGroup);
      setHasParent(true);
      SchemaService.getChildGroups(ID).then((response) => {
        const temp = { name, source_id: ID };
        temp.children = buildChildNodes(response.data);
        setActualGroup(temp);
      });

      callGtag('Abrir Grupo', dataUser.user_id, businessId, ID);
    },
    [actualGroup, businessId, dataUser.user_id]
  );

  const closeGroup = useCallback(
    (parentParam) => {
      if (parentParam.source_id === 'root') {
        setParent(null);
        setHasParent(false);
        SchemaService.getChildGroups('root').then((response) => {
          const temp = { source_id: 'root', name: 'Todos os grupos' };
          temp.children = buildChildNodes(response.data);
          setActualGroup(temp);
        });
      } else {
        setHasParent(true);
        SchemaService.getParents('1.1', parentParam.source_id).then((response) => {
          if (response.data.length === 0) {
            if (isAdmin) {
              const temp = {
                source_id: 'root',
                name: 'Todos os grupos',
              };
              setParent(temp);
            } else {
              setHasParent(false);
              setParent(null);
            }
          } else {
            setParent(response.data[0]);
          }
          SchemaService.getChildGroups(parent.source_id).then((responseParam) => {
            const copy = parent;
            copy.children = buildChildNodes(responseParam.data);
            setActualGroup(copy);
          });
        });
      }
    },
    [isAdmin, parent]
  );

  const selectGroup = useCallback(
    (ID) => {
      setSelectedGroup(ID);
      setShowSelector(!showSelector);
      changeCodeUrl(ID);
      if (ID !== 'root') {
        setHasParent(true);
      }
      callGtag('Selecionar Grupo', dataUser.user_id, businessId, ID);
    },
    [businessId, changeCodeUrl, dataUser.user_id, showSelector]
  );

  const toggleBreadcrumb = useCallback(() => {
    callGtag(showSelector ? 'Fechar Breadcrumb' : 'Abrir Breadcrumb', dataUser.user_id, businessId);
    setShowSelector(!showSelector);
  }, [businessId, dataUser.user_id, showSelector]);

  return (
    <>
      <S.BreadCrumbItens onClick={() => !breadCrumb.isLoading && toggleBreadcrumb()}>
        {data && data.currentCode ? (
          <Active name={data.currentCode.name} />
        ) : (
          <Active name={t('ALL_GROUPS')} />
        )}
      </S.BreadCrumbItens>
      {showSelector && (
        <ClickOutHandler
          events={['mousedown', 'touchstart', 'keydown']}
          onClickOut={(event) => {
            if (!breadCrumb.isLoading) {
              const isKey = event instanceof KeyboardEvent;
              const isEsc = event?.key === 'Escape';

              if (isEsc || !isKey) toggleBreadcrumb();
            }
          }}
        >
          <S.TooltipWrapper>
            <Tooltip orientation="top" align="left" padding="32px;" width="540px" border>
              <S.Wrapper>
                <S.Title>{t('SELECT_WHICH_ONE')}</S.Title>
                <S.SubTitle>{t('YOU_CAN_CHOOSE_ONE_GROUP')}</S.SubTitle>
                {actualGroup?.children?.length !== 0 && selectedGroup !== actualGroup.source_id && (
                  <S.SelectButton onClick={() => selectGroup(actualGroup.source_id)}>
                    {t('CHOOSE_THIS_LEVEL')}
                  </S.SelectButton>
                )}
                {actualGroup && (
                  <>
                    <GroupItem
                      key={actualGroup.source_id}
                      source_id={actualGroup.source_id}
                      name={actualGroup.name}
                      isOpen
                      hasParent={hasParent}
                      color={actualGroup.color}
                      openGroup={() => openGroup(actualGroup.name, actualGroup.source_id)}
                      closeGroup={() => closeGroup(parent)}
                    />
                    <S.InputWrapper>
                      <Formik>
                        <Field
                          component={InputDefault}
                          type="text"
                          id="search"
                          name="search"
                          placeholder={t('SEARCH')}
                          onChange={(e) => setSearch(e.target.value)}
                          value={search}
                          fontSize="px14"
                          icon={['fal', 'search']}
                          block
                        />
                      </Formik>
                    </S.InputWrapper>
                    <Scrollbars autoHeight>
                      {actualGroup?.children?.map(
                        (group) =>
                          group?.name?.toUpperCase().includes(search.toUpperCase()) && (
                            <GroupItem
                              key={group.source_id}
                              source_id={group.source_id}
                              name={group.name}
                              hasChild={group.hasChild}
                              isOpen={false}
                              color={group.color}
                              openGroup={() => openGroup(group.name, group.source_id)}
                              onSelect={() => selectGroup(group.source_id)}
                              selected={selectedGroup === group.source_id}
                            />
                          )
                      )}
                    </Scrollbars>
                  </>
                )}
              </S.Wrapper>
            </Tooltip>
          </S.TooltipWrapper>
        </ClickOutHandler>
      )}
    </>
  );
}

BreadCrumb.propTypes = {
  data: PropTypes.object,
};

BreadCrumb.defaultProps = {};

export default BreadCrumb;
